import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { type PipelineStage } from "@/lib/types";

interface KeyMetricsProps {
  stageStats: Record<PipelineStage, number>;
  totalProspects: number;
}

export function KeyMetrics({ stageStats, totalProspects }: KeyMetricsProps) {
  // Calculate conversion rates between stages
  const calculateConversionRate = (fromStage: PipelineStage, toStage: PipelineStage) => {
    const toCount = stageStats[toStage] || 0;
    
    // For cumulative conversion (e.g., how many new prospects become participants)
    if (fromStage === 'new' && toStage === 'participant') {
      return totalProspects > 0 ? Math.round((toCount / totalProspects) * 100) : 0;
    }
    
    // For stage-to-stage conversion
    const totalFromStageAndBeyond = getTotalFromStageOnward(fromStage);
    return totalFromStageAndBeyond > 0 ? Math.round((toCount / totalFromStageAndBeyond) * 100) : 0;
  };

  const getTotalFromStageOnward = (stage: PipelineStage): number => {
    const stages: PipelineStage[] = ['new', 'contacted', 'qualified', 'interested', 'committed', 'participant'];
    const stageIndex = stages.indexOf(stage);
    return stages.slice(stageIndex).reduce((sum, s) => sum + (stageStats[s] || 0), 0);
  };

  const conversionRates = {
    newToContacted: calculateConversionRate('new', 'contacted'),
    contactedToQualified: calculateConversionRate('contacted', 'qualified'),
    qualifiedToInterested: calculateConversionRate('qualified', 'interested'),
    interestedToCommitted: calculateConversionRate('interested', 'committed'),
    committedToParticipant: calculateConversionRate('committed', 'participant'),
    newToParticipant: calculateConversionRate('new', 'participant'),
  };

  // Calculate funnel efficiency
  const funnelSteps = [
    { stage: 'new', count: stageStats.new || 0, label: 'New' },
    { stage: 'contacted', count: stageStats.contacted || 0, label: 'Contacted' },
    { stage: 'qualified', count: stageStats.qualified || 0, label: 'Qualified' },
    { stage: 'interested', count: stageStats.interested || 0, label: 'Interested' },
    { stage: 'committed', count: stageStats.committed || 0, label: 'Committed' },
    { stage: 'participant', count: stageStats.participant || 0, label: 'Participant' },
  ];

  // Note: Weekly growth tracking will be implemented when historical data is available

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Conversion Rates */}
      <Card>
        <CardHeader>
          <CardTitle>Conversion Rates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm">New → Contacted</span>
              <span className="font-mono text-sm">{conversionRates.newToContacted}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Contacted → Qualified</span>
              <span className="font-mono text-sm">{conversionRates.contactedToQualified}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Qualified → Interested</span>
              <span className="font-mono text-sm">{conversionRates.qualifiedToInterested}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Interested → Committed</span>
              <span className="font-mono text-sm">{conversionRates.interestedToCommitted}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Committed → Participant</span>
              <span className="font-mono text-sm">{conversionRates.committedToParticipant}%</span>
            </div>
            <div className="pt-2 border-t flex justify-between items-center">
              <span className="text-sm font-medium">Overall (New → Participant)</span>
              <span className="font-mono text-sm font-bold">{conversionRates.newToParticipant}%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Funnel */}
      <Card>
        <CardHeader>
          <CardTitle>Pipeline Funnel</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {funnelSteps.map((step) => {
            const percentage = totalProspects > 0 ? (step.count / totalProspects) * 100 : 0;
            const width = Math.max(percentage, 5); // Minimum width for visibility
            
            return (
              <div key={step.stage} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>{step.label}</span>
                  <span className="font-mono">{step.count}</span>
                </div>
                <div className="bg-slate-200 rounded-full h-6 relative overflow-hidden">
                  <div 
                    className="bg-slate-600 h-full rounded-full transition-all duration-300"
                    style={{ width: `${width}%` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-medium text-white">
                      {Math.round(percentage)}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          
          <div className="pt-4 border-t">
            <div className="text-center">
              <div className="text-sm text-muted-foreground">Funnel Efficiency</div>
              <div className="text-xl font-bold">
                {conversionRates.newToParticipant}%
              </div>
              <div className="text-xs text-muted-foreground">
                End-to-end conversion
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}